import React, { Suspense, useLayoutEffect } from "react"
import { ThemeProvider } from "@mui/material/styles"
import PropTypes from "prop-types"
import { Route, Routes, useLocation } from "react-router-dom"
import { MsalProvider } from "@azure/msal-react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import ChatbotProvider from "./component/common/Chatbot/ChatbotContext"
import { BusinessProvider } from "./component/context/BusinessContext"
import { theme } from "./theme"
import { savePostLoginRedirect } from "./services/postLoginRedirect"
import SharePointCalendar from "./pages/WeeklyCTO/SharePointCalendar"

import "./assets/scss/style.scss"
const Home = React.lazy(() => import("./features/home/home"))
// Pages
const Login = React.lazy(() => import("./pages/login/Login"))
const Page404 = React.lazy(() => import("./pages/page404/Page404"))
const Page500 = React.lazy(() => import("./pages/page500/Page500"))

const queryClient = new QueryClient()

const App = ({ pca }) => {
  const location = useLocation()
  const normalizedPathname = location.pathname.replace(/\/+/g, "/")
  const needsPathNormalization = normalizedPathname !== location.pathname
  const isPublicCalendar = location.pathname.replace(/\/$/, "") === "/embed/campaign-calendar"
  const loading = (
    <div className="pt-3 text-center">
      <div className="sk-spinner sk-spinner-pulse"></div>
    </div>
  )
  useLayoutEffect(() => {
    if (needsPathNormalization) {
      const nextUrl = `${window.location.origin}${normalizedPathname}${window.location.search}${window.location.hash}`
      window.location.replace(nextUrl)
      return
    }

    document.documentElement.style.setProperty("--full-height", `${window.screen.height}px`)
    if (!isPublicCalendar) savePostLoginRedirect()
  }, [isPublicCalendar, needsPathNormalization, normalizedPathname])

  if (needsPathNormalization) {
    return null
  }

  if (isPublicCalendar) {
    return (
      <Routes>
        <Route path="/embed/campaign-calendar" element={<SharePointCalendar />} />
      </Routes>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <MsalProvider instance={pca}>
        <ThemeProvider theme={theme}>
          <BusinessProvider>
            <ChatbotProvider>
              <Suspense fallback={loading}>
                <Routes>
                  <Route exact path="/" name="Login" element={<Login />} />
                  <Route exact path="/404" name="Page 404" element={<Page404 />} />
                  <Route exact path="/500" name="Page 500" element={<Page500 />} />
                  <Route path="*" name="Home" element={<Home />} />
                </Routes>
              </Suspense>
            </ChatbotProvider>
          </BusinessProvider>
        </ThemeProvider>
      </MsalProvider>
    </QueryClientProvider>
  )
}
App.propTypes = {
  pca: PropTypes.object.isRequired,
}

export default App
